import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';
import { s } from './TissStyles';
import { StatusBadge, useToast } from './TissShared';

function CollapsibleSection({ title, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: '4px' }}>
      <div
        onClick={() => setOpen(!open)}
        style={s.collapsibleHeader}
      >
        <span style={s.collapsibleTitle}>
          <span style={{ ...s.collapsibleArrow, transform: open ? 'rotate(90deg)' : 'rotate(0)' }}>&#9654;</span>
          {title}
        </span>
      </div>
      {open && <div style={{ padding: '12px 0' }}>{children}</div>}
    </div>
  );
}

export default function TabConfig({ dataInicio, dataFim }) {
  const [config, setConfig] = useState({});
  const [prestadores, setPrestadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testando, setTestando] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const toast = useToast();

  // Combos TUSS state
  const [combos, setCombos] = useState([]);
  const [combosLoading, setCombosLoading] = useState(false);
  const [editingCombo, setEditingCombo] = useState(null);
  const [comboForm, setComboForm] = useState({ exame_pattern: '', label: '', codigos: '', descricoes: '', ativo: 1 });

  // Mapeamento state
  const [dados, setDados] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [filtroLocal, setFiltroLocal] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [filtroBusca, setFiltroBusca] = useState('');

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/tiss/config`),
      axios.get(`${API_URL}/tiss/prestadores`),
    ]).then(([cRes, pRes]) => {
      setConfig(cRes.data);
      setPrestadores(pRes.data);
    }).catch(err => console.error('Erro config:', err))
      .finally(() => setLoading(false));
  }, []);

  const salvarConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/tiss/config`, config);
      toast.success('Configuracoes salvas!');
    } catch (err) { toast.error('Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const salvarPrestador = async (p) => {
    try {
      await axios.put(`${API_URL}/tiss/prestadores/${p.local_id}`, p);
      toast.success(`Prestador ${p.local_nome} salvo`);
    } catch (err) { toast.error('Erro ao salvar prestador'); }
  };

  const testarConexao = async () => {
    setTestando(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API_URL}/tiss/config/testar-conexao`);
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ sucesso: false, mensagem: err.response?.data?.error || err.message });
    } finally { setTestando(false); }
  };

  // Combos TUSS
  const carregarCombos = useCallback(async () => {
    setCombosLoading(true);
    try {
      const res = await axios.get(`${API_URL}/tiss/combos`);
      setCombos(res.data);
    } catch (err) {
      console.error('Erro ao carregar combos:', err);
    } finally { setCombosLoading(false); }
  }, []);

  useEffect(() => { carregarCombos(); }, [carregarCombos]);

  const iniciarEdicaoCombo = (combo) => {
    let codigosStr = '';
    try { const arr = JSON.parse(combo.codigos); codigosStr = arr.join(', '); } catch { codigosStr = combo.codigos; }
    let descStr = '';
    try { const obj = JSON.parse(combo.descricoes || '{}'); descStr = Object.entries(obj).map(([k, v]) => `${k}:${v}`).join(', '); } catch { descStr = combo.descricoes || ''; }
    setEditingCombo(combo.id);
    setComboForm({ exame_pattern: combo.exame_pattern, label: combo.label || '', codigos: codigosStr, descricoes: descStr, ativo: combo.ativo });
  };

  const iniciarNovoCombo = () => {
    setEditingCombo('new');
    setComboForm({ exame_pattern: '', label: '', codigos: '', descricoes: '', ativo: 1 });
  };

  const cancelarEdicaoCombo = () => {
    setEditingCombo(null);
    setComboForm({ exame_pattern: '', label: '', codigos: '', descricoes: '', ativo: 1 });
  };

  const salvarCombo = async () => {
    if (!comboForm.exame_pattern.trim() || !comboForm.codigos.trim()) {
      toast.warning('Pattern e codigos sao obrigatorios');
      return;
    }
    const codigosArr = comboForm.codigos.split(',').map(c => c.trim()).filter(Boolean);
    const codigosJson = JSON.stringify(codigosArr);
    const descObj = {};
    if (comboForm.descricoes.trim()) {
      comboForm.descricoes.split(',').forEach(part => {
        const idx = part.indexOf(':');
        if (idx > 0) { descObj[part.substring(0, idx).trim()] = part.substring(idx + 1).trim(); }
      });
    }
    const descJson = JSON.stringify(descObj);
    const payload = { exame_pattern: comboForm.exame_pattern.trim(), label: comboForm.label.trim(), codigos: codigosJson, descricoes: descJson, ativo: comboForm.ativo };
    try {
      if (editingCombo === 'new') {
        await axios.post(`${API_URL}/tiss/combos`, payload);
      } else {
        await axios.put(`${API_URL}/tiss/combos/${editingCombo}`, payload);
      }
      cancelarEdicaoCombo();
      carregarCombos();
      toast.success('Combo salvo!');
    } catch (err) {
      toast.error('Erro ao salvar combo: ' + (err.response?.data?.error || err.message));
    }
  };

  const excluirCombo = async (id) => {
    if (!window.confirm('Excluir este combo?')) return;
    try {
      await axios.delete(`${API_URL}/tiss/combos/${id}`);
      carregarCombos();
      toast.success('Combo excluido');
    } catch (err) {
      toast.error('Erro ao excluir: ' + (err.response?.data?.error || err.message));
    }
  };

  const formatCodigos = (codigosJson) => {
    try { const arr = JSON.parse(codigosJson); return arr.join(', '); } catch { return codigosJson; }
  };

  // Mapeamento
  const carregarMapeamento = useCallback(async () => {
    setMapLoading(true);
    try {
      const params = {};
      if (filtroLocal) params.local_id = filtroLocal;
      if (dataInicio) params.dataInicio = dataInicio;
      if (dataFim) params.dataFim = dataFim;
      const res = await axios.get(`${API_URL}/tiss/mapeamento`, { params });
      setDados(res.data);
    } catch (err) {
      console.error('Erro ao carregar mapeamento:', err);
    } finally { setMapLoading(false); }
  }, [filtroLocal, dataInicio, dataFim]);

  useEffect(() => { carregarMapeamento(); }, [carregarMapeamento]);

  const examesFiltrados = dados?.exames?.filter(e => {
    if (filtroStatus === 'problemas' && e.status === 'ok') return false;
    if (filtroStatus !== 'todos' && filtroStatus !== 'problemas' && e.status !== filtroStatus) return false;
    if (filtroBusca) {
      const termo = filtroBusca.toLowerCase();
      const matchCodigo = (e.laudo_id || '').toLowerCase().includes(termo);
      const matchDesc = (e.descricao || '').toLowerCase().includes(termo);
      const matchTuss = (e.codigo_tuss || '').includes(termo);
      if (!matchCodigo && !matchDesc && !matchTuss) return false;
    }
    return true;
  }) || [];

  const resumo = dados?.resumo || { total_exames: 0, com_codigo_tuss: 0, sem_codigo: 0, codigo_invalido: 0 };

  const statusBadge = (status) => {
    const map = {
      ok: { bg: '#e8f5e9', color: '#2e7d32', label: 'OK' },
      sem_codigo: { bg: '#ffebee', color: '#c62828', label: 'Sem codigo' },
      codigo_invalido: { bg: '#fff3e0', color: '#e65100', label: 'Invalido' },
    };
    const st = map[status] || map.ok;
    return <span style={{ ...s.badge, backgroundColor: st.bg, color: st.color }}>{st.label}</span>;
  };

  if (loading) return <div style={s.emptyState}>Carregando...</div>;

  return (
    <div style={s.card}>
      {/* Section 1: Conexao SOAP */}
      <CollapsibleSection title="Conexao SOAP" defaultOpen>
        <div style={s.row}>
          <div style={s.col}>
            <label style={s.filterLabel}>Ambiente</label>
            <select style={{ ...s.select, width: '100%' }} value={config.ambiente || 'homologacao'}
              onChange={e => setConfig({ ...config, ambiente: e.target.value })}>
              <option value="homologacao">Homologacao</option>
              <option value="producao">Producao</option>
            </select>
          </div>
          <div style={s.col}>
            <label style={s.filterLabel}>Login</label>
            <input style={s.input} value={config.login || ''} onChange={e => setConfig({ ...config, login: e.target.value })} />
          </div>
          <div style={s.col}>
            <label style={s.filterLabel}>Senha</label>
            <input style={s.input} type="password" value={config.senha || ''} onChange={e => setConfig({ ...config, senha: e.target.value })} />
          </div>
          <div style={s.col}>
            <label style={s.filterLabel}>Registro ANS</label>
            <input style={s.input} value={config.registro_ans || ''} onChange={e => setConfig({ ...config, registro_ans: e.target.value })} />
          </div>
        </div>
        <div style={s.row}>
          <div style={{ ...s.col, minWidth: '400px' }}>
            <label style={s.filterLabel}>Endpoint HML</label>
            <input style={s.input} value={config.endpoint_base_hml || ''} onChange={e => setConfig({ ...config, endpoint_base_hml: e.target.value })} />
          </div>
          <div style={{ ...s.col, minWidth: '400px' }}>
            <label style={s.filterLabel}>Endpoint PROD</label>
            <input style={s.input} value={config.endpoint_base_prod || ''} onChange={e => setConfig({ ...config, endpoint_base_prod: e.target.value })} />
          </div>
        </div>
        <div style={s.row}>
          <div style={{ ...s.col, minWidth: '400px' }}>
            <label style={s.filterLabel}>Proxy TISS (opcional)</label>
            <input style={s.input} value={config.endpoint_proxy || ''} onChange={e => setConfig({ ...config, endpoint_proxy: e.target.value })}
              placeholder="Ex: https://servidor-br:8443/tiss/" />
            <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>
              Roteia o trafego TISS por um proxy intermediario. Util quando o servidor de producao tem IP bloqueado pela operadora. Deixe vazio para conexao direta.
            </div>
          </div>
          <div style={{ ...s.col, display: 'flex', alignItems: 'center', paddingTop: '20px' }}>
            {config.endpoint_proxy && (
              <span style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '12px', backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: '600' }}>
                Proxy ativo
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
          <button style={s.btnPrimary} onClick={testarConexao} disabled={testando}>
            {testando ? 'Testando...' : 'Testar Conexao'}
          </button>
        </div>
        {testResult && (
          <div style={{
            marginTop: '12px', padding: '12px', borderRadius: '6px',
            backgroundColor: testResult.sucesso ? '#e8f5e9' : '#ffebee',
            border: `1px solid ${testResult.sucesso ? '#c8e6c9' : '#ffcdd2'}`,
            fontSize: '13px',
          }}>
            <strong>{testResult.sucesso ? 'Sucesso' : 'Falha'}:</strong> {testResult.mensagem}
            {testResult.duracao_ms && <span style={{ color: '#888' }}> ({testResult.duracao_ms}ms)</span>}
          </div>
        )}
      </CollapsibleSection>

      {/* Section 2: Envio Automatico */}
      <CollapsibleSection title="Envio Automatico (Worker)">
        <div style={s.row}>
          <div style={s.col}>
            <label style={s.filterLabel}>Intervalo (minutos)</label>
            <input style={s.input} type="number" value={config.intervalo_minutos || '5'}
              onChange={e => setConfig({ ...config, intervalo_minutos: e.target.value })} />
          </div>
          <div style={s.col}>
            <label style={s.filterLabel}>Horario Inicio</label>
            <input style={s.input} type="time" value={config.horario_inicio || '07:00'}
              onChange={e => setConfig({ ...config, horario_inicio: e.target.value })} />
          </div>
          <div style={s.col}>
            <label style={s.filterLabel}>Horario Fim</label>
            <input style={s.input} type="time" value={config.horario_fim || '22:00'}
              onChange={e => setConfig({ ...config, horario_fim: e.target.value })} />
          </div>
          <div style={s.col}>
            <label style={s.filterLabel}>Dias Semana</label>
            <input style={s.input} value={config.dias_semana || '1,2,3,4,5'} placeholder="1,2,3,4,5"
              onChange={e => setConfig({ ...config, dias_semana: e.target.value })} />
          </div>
        </div>
      </CollapsibleSection>

      {/* Section 3: Prestadores */}
      <CollapsibleSection title="Prestadores (por Local)">
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Local</th>
              <th style={s.th}>Nome</th>
              <th style={s.th}>CNPJ</th>
              <th style={s.th}>CNES</th>
              <th style={s.th}>Cod. Operadora</th>
              <th style={s.th}>Principal</th>
              <th style={s.th}>Usar Principal</th>
              <th style={s.th}>Ativo</th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {prestadores.map((p, i) => (
              <tr key={p.id}>
                <td style={s.td}>{p.local_id}</td>
                <td style={s.td}>{p.local_nome}</td>
                <td style={s.td}>
                  <input style={{ ...s.input, width: '160px' }} value={p.cnpj || ''}
                    onChange={e => { const updated = [...prestadores]; updated[i] = { ...p, cnpj: e.target.value }; setPrestadores(updated); }} />
                </td>
                <td style={s.td}>
                  <input style={{ ...s.input, width: '100px' }} value={p.cnes || ''}
                    onChange={e => { const updated = [...prestadores]; updated[i] = { ...p, cnes: e.target.value }; setPrestadores(updated); }} />
                </td>
                <td style={s.td}>
                  <input style={{ ...s.input, width: '100px' }} value={p.codigo_operadora || ''}
                    onChange={e => { const updated = [...prestadores]; updated[i] = { ...p, codigo_operadora: e.target.value }; setPrestadores(updated); }} />
                </td>
                <td style={{ ...s.td, textAlign: 'center' }}>
                  <input type="radio" name="prestador_principal"
                    checked={p.principal === true}
                    onChange={() => {
                      const updated = prestadores.map((pr, j) => ({
                        ...pr, principal: j === i, usar_principal: j === i ? false : pr.usar_principal,
                      }));
                      setPrestadores(updated);
                    }} />
                </td>
                <td style={{ ...s.td, textAlign: 'center' }}>
                  <input type="checkbox"
                    checked={p.usar_principal === true}
                    disabled={p.principal === true}
                    onChange={e => { const updated = [...prestadores]; updated[i] = { ...p, usar_principal: e.target.checked }; setPrestadores(updated); }} />
                </td>
                <td style={s.td}>
                  <input type="checkbox" checked={p.ativo === 1}
                    onChange={e => { const updated = [...prestadores]; updated[i] = { ...p, ativo: e.target.checked ? 1 : 0 }; setPrestadores(updated); }} />
                </td>
                <td style={s.td}>
                  <button style={s.btnSmall} onClick={() => salvarPrestador(p)}>Salvar</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CollapsibleSection>

      {/* Section 4: Combos TUSS */}
      <CollapsibleSection title="Combos TUSS">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Exames combo sao expandidos em codigos TUSS individuais no envio de guias.
          </div>
          <button style={s.btnPrimary} onClick={iniciarNovoCombo} disabled={editingCombo === 'new'}>
            + Novo Combo
          </button>
        </div>
        {combosLoading ? (
          <div style={s.emptyState}><div style={s.emptyText}>Carregando combos...</div></div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Pattern</th>
                <th style={s.th}>Label</th>
                <th style={s.th}>Codigos TUSS</th>
                <th style={s.th}>Descricoes</th>
                <th style={{ ...s.th, textAlign: 'center', width: '60px' }}>Ativo</th>
                <th style={{ ...s.th, textAlign: 'center', width: '120px' }}>Acoes</th>
              </tr>
            </thead>
            <tbody>
              {editingCombo === 'new' && (
                <tr style={{ backgroundColor: '#f0f7ff' }}>
                  <td style={s.td}><input style={{ ...s.input, width: '100%' }} placeholder="Ex: COLEST.TOTAL/FRAC" value={comboForm.exame_pattern} onChange={e => setComboForm(f => ({ ...f, exame_pattern: e.target.value }))} /></td>
                  <td style={s.td}><input style={{ ...s.input, width: '100%' }} placeholder="Descritivo" value={comboForm.label} onChange={e => setComboForm(f => ({ ...f, label: e.target.value }))} /></td>
                  <td style={s.td}><input style={{ ...s.input, width: '100%' }} placeholder="40301583, 40302547, ..." value={comboForm.codigos} onChange={e => setComboForm(f => ({ ...f, codigos: e.target.value }))} /></td>
                  <td style={s.td}><input style={{ ...s.input, width: '100%' }} placeholder="40301583:HDL, 40302547:TGR" value={comboForm.descricoes} onChange={e => setComboForm(f => ({ ...f, descricoes: e.target.value }))} /></td>
                  <td style={{ ...s.td, textAlign: 'center' }}><input type="checkbox" checked={comboForm.ativo === 1} onChange={e => setComboForm(f => ({ ...f, ativo: e.target.checked ? 1 : 0 }))} /></td>
                  <td style={{ ...s.td, textAlign: 'center' }}>
                    <button style={{ ...s.btnSmall, color: '#2e7d32', borderColor: '#2e7d32', marginRight: '4px' }} onClick={salvarCombo}>Salvar</button>
                    <button style={s.btnSmall} onClick={cancelarEdicaoCombo}>Cancelar</button>
                  </td>
                </tr>
              )}
              {combos.map(combo => (
                <tr key={combo.id} style={{ backgroundColor: combo.ativo ? '#fff' : '#f9f9f9' }}>
                  {editingCombo === combo.id ? (
                    <>
                      <td style={s.td}><input style={{ ...s.input, width: '100%' }} value={comboForm.exame_pattern} onChange={e => setComboForm(f => ({ ...f, exame_pattern: e.target.value }))} /></td>
                      <td style={s.td}><input style={{ ...s.input, width: '100%' }} value={comboForm.label} onChange={e => setComboForm(f => ({ ...f, label: e.target.value }))} /></td>
                      <td style={s.td}><input style={{ ...s.input, width: '100%' }} value={comboForm.codigos} onChange={e => setComboForm(f => ({ ...f, codigos: e.target.value }))} /></td>
                      <td style={s.td}><input style={{ ...s.input, width: '100%' }} value={comboForm.descricoes} onChange={e => setComboForm(f => ({ ...f, descricoes: e.target.value }))} /></td>
                      <td style={{ ...s.td, textAlign: 'center' }}><input type="checkbox" checked={comboForm.ativo === 1} onChange={e => setComboForm(f => ({ ...f, ativo: e.target.checked ? 1 : 0 }))} /></td>
                      <td style={{ ...s.td, textAlign: 'center' }}>
                        <button style={{ ...s.btnSmall, color: '#2e7d32', borderColor: '#2e7d32', marginRight: '4px' }} onClick={salvarCombo}>Salvar</button>
                        <button style={s.btnSmall} onClick={cancelarEdicaoCombo}>Cancelar</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={{ ...s.td, fontFamily: 'monospace', fontWeight: '600', fontSize: '12px' }}>{combo.exame_pattern}</td>
                      <td style={s.td}>{combo.label || <span style={{ color: '#999' }}>-</span>}</td>
                      <td style={{ ...s.td, fontFamily: 'monospace', fontSize: '11px', maxWidth: '300px', wordBreak: 'break-all' }}>{formatCodigos(combo.codigos)}</td>
                      <td style={{ ...s.td, fontSize: '11px', maxWidth: '200px', wordBreak: 'break-all' }}>
                        {(() => { try { const d = JSON.parse(combo.descricoes || '{}'); const entries = Object.entries(d); return entries.length > 0 ? entries.map(([k,v]) => `${k}:${v}`).join(', ') : <span style={{ color: '#999' }}>-</span>; } catch { return <span style={{ color: '#999' }}>-</span>; } })()}
                      </td>
                      <td style={{ ...s.td, textAlign: 'center' }}>
                        <span style={{ ...s.badge, backgroundColor: combo.ativo ? '#e8f5e9' : '#ffebee', color: combo.ativo ? '#2e7d32' : '#c62828' }}>
                          {combo.ativo ? 'Sim' : 'Nao'}
                        </span>
                      </td>
                      <td style={{ ...s.td, textAlign: 'center' }}>
                        <button style={{ ...s.btnSmall, marginRight: '4px' }} onClick={() => iniciarEdicaoCombo(combo)}>Editar</button>
                        <button style={{ ...s.btnSmall, color: '#d32f2f', borderColor: '#d32f2f' }} onClick={() => excluirCombo(combo.id)}>Excluir</button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {combos.length === 0 && !combosLoading && (
                <tr><td colSpan="6" style={{ ...s.td, textAlign: 'center', color: '#999' }}>Nenhum combo cadastrado</td></tr>
              )}
            </tbody>
          </table>
        )}
      </CollapsibleSection>

      {/* Section 5: Mapeamento de Exames */}
      <CollapsibleSection title="Mapeamento de Exames (Diagnostico TUSS)">
        <div style={s.statsRow}>
          <div style={{ ...s.statCard, borderLeftColor: '#1976d2', cursor: 'default' }}>
            <div style={s.statLabel}>Total Exames</div>
            <div style={s.statValue}>{resumo.total_exames}</div>
          </div>
          <div style={{ ...s.statCard, borderLeftColor: '#2e7d32', cursor: 'default' }}>
            <div style={s.statLabel}>Com TUSS</div>
            <div style={{ ...s.statValue, color: '#2e7d32' }}>{resumo.com_codigo_tuss}</div>
          </div>
          <div style={{ ...s.statCard, borderLeftColor: '#c62828', cursor: 'default' }}>
            <div style={s.statLabel}>Sem Codigo</div>
            <div style={{ ...s.statValue, color: '#c62828' }}>{resumo.sem_codigo}</div>
          </div>
          <div style={{ ...s.statCard, borderLeftColor: '#e65100', cursor: 'default' }}>
            <div style={s.statLabel}>Codigo Invalido</div>
            <div style={{ ...s.statValue, color: '#e65100' }}>{resumo.codigo_invalido}</div>
          </div>
        </div>

        <div style={{ ...s.filtersRow, boxShadow: 'none', padding: '0', backgroundColor: 'transparent', marginBottom: '12px' }}>
          <div style={{ ...s.filterGroup, minWidth: '200px', flex: 2 }}>
            <label style={s.filterLabel}>Buscar exame</label>
            <input style={s.input} type="text" placeholder="Codigo, descricao ou TUSS..."
              value={filtroBusca} onChange={e => setFiltroBusca(e.target.value)} />
          </div>
          <div style={s.filterGroup}>
            <label style={s.filterLabel}>Local</label>
            <select style={s.select} value={filtroLocal} onChange={e => setFiltroLocal(e.target.value)}>
              <option value="">Todos</option>
              <option value="01">Matriz</option>
              <option value="02">Itapecerica</option>
              <option value="04">Pedra do Indaia</option>
              <option value="CA">Camacho</option>
            </select>
          </div>
          <button style={s.btnPrimary} onClick={carregarMapeamento} disabled={mapLoading}>
            {mapLoading ? 'Carregando...' : 'Buscar'}
          </button>
        </div>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
          {[
            { id: 'todos', label: 'Todos', count: resumo.total_exames },
            { id: 'ok', label: 'OK', count: resumo.com_codigo_tuss },
            { id: 'problemas', label: 'So problemas', count: resumo.sem_codigo + resumo.codigo_invalido },
            ...(resumo.sem_codigo > 0 && resumo.codigo_invalido > 0 ? [
              { id: 'sem_codigo', label: 'Sem codigo', count: resumo.sem_codigo },
              { id: 'codigo_invalido', label: 'Codigo invalido', count: resumo.codigo_invalido },
            ] : []),
          ].map(f => (
            <button key={f.id}
              style={{
                ...s.btnSmall,
                backgroundColor: filtroStatus === f.id ? '#1976d2' : '#fff',
                color: filtroStatus === f.id ? '#fff' : '#555',
                borderColor: filtroStatus === f.id ? '#1976d2' : '#ddd',
              }}
              onClick={() => setFiltroStatus(f.id)}>
              {f.label} ({f.count})
            </button>
          ))}
        </div>

        {mapLoading ? (
          <div style={s.emptyState}><div style={s.emptyText}>Carregando mapeamento...</div></div>
        ) : examesFiltrados.length === 0 ? (
          <div style={s.emptyState}>
            <div style={s.emptyIcon}>&#128269;</div>
            <div style={s.emptyText}>Nenhum exame encontrado</div>
            <div style={s.emptyHint}>Ajuste os filtros ou verifique se ha atendimentos UNIMED no periodo</div>
          </div>
        ) : (
          <>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Cod. Interno</th>
                  <th style={s.th}>Descricao</th>
                  <th style={s.th}>Codigo TUSS</th>
                  <th style={s.th}>Status</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Qtd Solic.</th>
                </tr>
              </thead>
              <tbody>
                {examesFiltrados.map((ex, i) => (
                  <tr key={i} style={{ ...s.trHover, backgroundColor: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ ...s.td, fontFamily: 'monospace', fontWeight: '600', fontSize: '12px' }}>{ex.laudo_id}</td>
                    <td style={s.td}>{ex.descricao}</td>
                    <td style={{ ...s.td, fontFamily: 'monospace' }}>
                      {ex.codigo_tuss || <span style={{ color: '#c62828', fontStyle: 'italic' }}>-</span>}
                    </td>
                    <td style={s.td}>{statusBadge(ex.status)}</td>
                    <td style={{ ...s.td, textAlign: 'right', fontWeight: '600' }}>{ex.qtd_solicitacoes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {examesFiltrados.length > 0 && (
              <div style={{ fontSize: '12px', color: '#999', marginTop: '12px', textAlign: 'right' }}>
                Exibindo {examesFiltrados.length} de {resumo.total_exames} exames
              </div>
            )}
          </>
        )}
      </CollapsibleSection>

      {/* Save all */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
        <button style={s.btnPrimary} onClick={salvarConfig} disabled={saving}>
          {saving ? 'Salvando...' : 'Salvar Configuracoes'}
        </button>
      </div>
    </div>
  );
}
